import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Modal,
  Alert,
  CircularProgress,
  Paper,
  Grid
} from '@mui/material';
import {
  Person,
  Add,
  Remove,
  Visibility,
  VisibilityOff,
  PersonAdd,
  PersonRemove,
  Group
} from '@mui/icons-material';
import apiService from '../../services/apiService';
import { useToast } from '../common/ToastProvider';

function GhostUserManagementCard({ companyId }) {
  const { showSuccess, showError } = useToast();
  const [ghostUsers, setGhostUsers] = useState([]);
  const [isCurrentUserGhost, setIsCurrentUserGhost] = useState(false);
  const [loading, setLoading] = useState(true);
  const [addGhostLoading, setAddGhostLoading] = useState(false);
  const [removeGhostLoading, setRemoveGhostLoading] = useState(false);
  const [addGhostModalOpen, setAddGhostModalOpen] = useState(false);
  const [removeGhostModalOpen, setRemoveGhostModalOpen] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchGhostData();
  }, [companyId]);

  const fetchGhostData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch ghost users and current user's ghost status in parallel
      const [ghostUsersResult, ghostStatusResult] = await Promise.all([
        apiService.fetchGhostUsers(companyId),
        apiService.checkGhostStatus(companyId)
      ]);

      if (ghostUsersResult.success) {
        setGhostUsers(ghostUsersResult.data.ghost_users || []);
      } else {
        console.error('Failed to fetch ghost users:', ghostUsersResult.error);
        setError('Failed to load ghost users');
      }

      if (ghostStatusResult.success) {
        setIsCurrentUserGhost(ghostStatusResult.data.is_ghost);
      } else {
        console.error('Failed to check ghost status:', ghostStatusResult.error);
      }
    } catch (error) {
      console.error('Error fetching ghost data:', error);
      setError('Failed to load ghost user data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddGhostClick = () => {
    setAddGhostModalOpen(true);
  };

  const handleRemoveGhostClick = () => {
    setRemoveGhostModalOpen(true);
  };

  const handleAddGhostConfirm = async () => {
    setAddGhostLoading(true);
    setAddGhostModalOpen(false);

    try {
      const result = await apiService.addGhostToCompany(companyId);

      if (result.success) {
        console.log('Successfully added as ghost:', result.data);
        setIsCurrentUserGhost(true);
        await fetchGhostData(); // Refresh the list
        showSuccess(result.data.message || 'Successfully added as ghost user', 'Success');
      } else {
        console.error('Failed to add ghost:', result.error);
        showError(`Failed to add ghost: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error adding ghost:', error);
      showError('Failed to add ghost user. Please try again.', 'Error');
    } finally {
      setAddGhostLoading(false);
    }
  };

  const handleRemoveGhostConfirm = async () => {
    setRemoveGhostLoading(true);
    setRemoveGhostModalOpen(false);

    try {
      const result = await apiService.removeGhostFromCompany(companyId);

      if (result.success) {
        console.log('Successfully removed as ghost:', result.data);
        setIsCurrentUserGhost(false);
        await fetchGhostData(); // Refresh the list
        showSuccess(result.data.message || 'Successfully removed as ghost user', 'Success');
      } else {
        console.error('Failed to remove ghost:', result.error);
        showError(`Failed to remove ghost: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error removing ghost:', error);
      showError('Failed to remove ghost user. Please try again.', 'Error');
    } finally {
      setRemoveGhostLoading(false);
    }
  };



  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getUserDisplayName = (user) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user.first_name) {
      return user.first_name;
    }
    if (user.user_name) {
      return user.user_name;
    }
    return user.email || 'Unknown User';
  };

  const getUserInitials = (user) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user.first_name) {
      return user.first_name[0].toUpperCase();
    }
    if (user.user_name) {
      return user.user_name[0].toUpperCase();
    }
    return '?';
  };

  if (loading) {
    return (
      <Card sx={{ width: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Group sx={{ color: 'primary.main' }} />
              <Typography variant="h6" fontWeight={600}>
                👻 Ghost User Management
              </Typography>
            </Box>
            <Chip 
              label="Loading..."
              color="primary"
              variant="outlined"
            />
          </Box>
          
          <Divider sx={{ mb: 3 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={40} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ width: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" fontWeight={600}>
                👻 Ghost User Management
              </Typography>
            </Box>
            <Chip 
              label={`${ghostUsers.length} ghost user${ghostUsers.length !== 1 ? 's' : ''}`}
              color="primary"
              variant="outlined"
            />
          </Box>
          
          <Divider sx={{ mb: 3 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Current User Ghost Status */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isCurrentUserGhost ? (
                    <Visibility sx={{ color: 'success.main' }} />
                  ) : (
                    <VisibilityOff sx={{ color: 'text.secondary' }} />
                  )}
                  <Typography variant="body1" fontWeight={500}>
                    Your Ghost Status:
                  </Typography>
                </Box>
                <Chip 
                  label={isCurrentUserGhost ? 'Ghost User' : 'Not a Ghost User'} 
                  color={isCurrentUserGhost ? 'success' : 'default'} 
                  variant="filled"
                  size="small"
                />
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                {isCurrentUserGhost ? (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<PersonRemove />}
                    onClick={handleRemoveGhostClick}
                    disabled={removeGhostLoading}
                  >
                    {removeGhostLoading ? 'Removing...' : 'Remove Me'}
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<PersonAdd />}
                    onClick={handleAddGhostClick}
                    disabled={addGhostLoading}
                  >
                    {addGhostLoading ? 'Adding...' : 'Add Me'}
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>

          {/* Ghost Users List */}
          <Box>
            
            {ghostUsers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Person sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  No ghost users found for this company
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Ghost users can access this company and all its projects invisibly
                </Typography>
              </Box>
            ) : (
              <List sx={{ bgcolor: 'background.default', borderRadius: 1 }}>
                {ghostUsers.map((user, index) => (
                  <ListItem
                    key={user.user_id}
                    sx={{
                      borderBottom: index < ghostUsers.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      '&:last-child': {
                        borderBottom: 'none'
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {getUserInitials(user)}
                      </Avatar>
                    </ListItemAvatar>
                    
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" fontWeight={500}>
                            {getUserDisplayName(user)}
                          </Typography>
                          {user.user_id === 'current_user' && (
                            <Chip label="You" size="small" color="primary" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Added: {formatDate(user.added_date)}
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip 
                              label={`ID: ${user.user_id}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                            />
                          </Box>
                        </Box>
                      }
                    />
                    
                    <ListItemSecondaryAction>
                      <Chip 
                        label="Ghost"
                        color="success"
                        size="small"
                        variant="filled"
                        icon={<Visibility />}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Add Ghost Confirmation Modal */}
      <Modal
        open={addGhostModalOpen}
        onClose={() => setAddGhostModalOpen(false)}
        aria-labelledby="add-ghost-modal"
        aria-describedby="add-ghost-confirmation"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PersonAdd sx={{ color: 'primary.main' }} />
            <Typography variant="h6">
              Add Ghost Access
            </Typography>
          </Box>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Adding yourself as a ghost user will give you invisible access to:
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              <li>This company</li>
              <li>All projects (organizations) in this company</li>
            </Box>
          </Alert>

          <Typography variant="body1" sx={{ mb: 3 }}>
            You will be able to view and interact with the company's data without being visible to regular users.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined" 
              onClick={() => setAddGhostModalOpen(false)}
              disabled={addGhostLoading}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              startIcon={<PersonAdd />}
              onClick={handleAddGhostConfirm}
              disabled={addGhostLoading}
              sx={{ minWidth: 100 }}
            >
              {addGhostLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  Adding...
                </Box>
              ) : (
                'Add Ghost'
              )}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Remove Ghost Confirmation Modal */}
      <Modal
        open={removeGhostModalOpen}
        onClose={() => setRemoveGhostModalOpen(false)}
        aria-labelledby="remove-ghost-modal"
        aria-describedby="remove-ghost-confirmation"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PersonRemove sx={{ color: 'error.main' }} />
            <Typography variant="h6" color="error">
              Remove Ghost Access
            </Typography>
          </Box>
          
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Removing ghost access will:
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              <li>Remove your access to this company</li>
              <li>Remove your access to all projects in this company</li>
              <li>Remove all your roles and permissions</li>
              <li>You will no longer be able to view company data</li>
            </Box>
          </Alert>

          <Typography variant="body1" sx={{ mb: 3 }}>
            Are you sure you want to remove your ghost access to this company?
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined" 
              onClick={() => setRemoveGhostModalOpen(false)}
              disabled={removeGhostLoading}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="error"
              startIcon={<PersonRemove />}
              onClick={handleRemoveGhostConfirm}
              disabled={removeGhostLoading}
              sx={{ minWidth: 100 }}
            >
              {removeGhostLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  Removing...
                </Box>
              ) : (
                'Remove Ghost'
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export default GhostUserManagementCard; 